
import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const HowWeWork = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const steps = [
    {
      number: "01",
      title: "Understanding & Planning",
      description: "We begin by analyzing your business needs, goals, and target audience. Through detailed discussions, we identify the best strategies and create a tailored plan to ensure maximum impact and effectiveness.",
      icon: "🎯",
      delay: 0
    },
    {
      number: "02",
      title: "Execution & Development",
      description: "Our expert team brings the plan to life by designing, developing, and implementing high-quality solutions. Whether it's a website, digital marketing campaign, video production, or branding, we ensure precision and creativity in every step.",
      icon: "⚙️",
      delay: 0.2
    },
    {
      number: "03",
      title: "Delivery & Support",
      description: "Once the project is completed, we deliver the final product and ensure a smooth launch. We also provide ongoing support, updates, and optimizations to help you achieve long-term success and sustainable growth.",
      icon: "🚀",
      delay: 0.4
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 px-6" id="how-we-work">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="section-heading">How We <span className="text-blue-500">Work</span></h2>
          <p className="section-subheading">Our proven process to deliver exceptional results for your business</p>
        </div>

        <div className="relative">
          {/* Timeline Line (Desktop) */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 transform -translate-y-1/2 rounded-full"></div>
          
          {/* Timeline Line (Mobile) */}
          <div className="md:hidden absolute top-0 bottom-0 left-8 w-1 bg-gradient-to-b from-blue-200 via-blue-400 to-blue-200 rounded-full"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: step.delay }}
                className="relative"
              >
                {/* Step Number (Desktop) */}
                <div className="hidden md:flex absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-white border-4 border-blue-500 text-blue-500 font-bold text-lg items-center justify-center z-10">
                  {step.number}
                </div>
                
                {/* Step Number (Mobile) */}
                <div className="md:hidden absolute left-0 top-0 w-16 h-16 rounded-full bg-white border-4 border-blue-500 text-blue-500 font-bold text-lg flex items-center justify-center z-10">
                  {step.number}
                </div>
                
                <div className="md:pt-8 md:px-4 pl-20 md:pl-0 glass-card rounded-xl p-6 md:text-center">
                  <div className="text-3xl md:text-4xl mb-4">{step.icon}</div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        <div className="mt-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h3 className="text-2xl font-bold mb-6">Ready to Start Your Project?</h3>
            <Link 
              to="/contact"
              className="inline-block px-10 py-4 bg-gradient-to-r from-blue-600 via-purple-500 to-blue-700 hover:from-blue-700 hover:via-purple-600 hover:to-blue-800 text-white font-bold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 animate-pulse"
            >
              <span className="relative z-10 flex items-center">
                <span className="mr-2">Start Your Project Today</span>
                <svg className="w-5 h-5 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;
